/**
 * @file     attributionCookies.ts
 * @purpose  Stamps the visitor-identity and first-touch attribution cookies
 *           from the server. Safari's ITP caps script-written cookies at seven
 *           days whatever max-age they carry, so the SDK's two-year visitor id
 *           was a one-week id on iOS and every first-touch campaign expired
 *           with it. Cookies set over HTTP are not capped. Readable by script
 *           (httpOnly false) because the browser SDK still reads them.
 *
 *           A Set-Cookie makes the response personal, and a personal response
 *           cannot be CDN-cached. Minting an id on every cookieless GET meant
 *           every crawler fetch (crawlers never send cookies) was a cache miss
 *           and a full render, on every HTML page and every sitemap. So the
 *           identity pair is only minted when the request already says
 *           something worth remembering (a click id, a campaign, a share ref,
 *           a consent choice, a non-GET), never for a crawler, and never on a
 *           crawler feed. A plain first page view gets its id from the browser
 *           SDK on its first tracked event (relta/client.ts, gtagBootScript.ts),
 *           and the SDK's first beacon to the analytics ingest re-issues the
 *           same values over HTTP, which lifts ITP's seven-day cap.
 */

import { isSignaturePublicPath } from '@/core/config/signaturePaths';
import type { NextRequest, NextResponse } from 'next/server';
import { readClickId, readCampaign, readShareRef } from './attribution';
import {
  CONSENT_COOKIE,
  consentMaxAgeLeft,
  isOptInRegion,
  parseConsent,
  REGION_COOKIE,
  REGION_MAX_AGE_S,
  serializeConsent,
  visitorRegion,
} from './consent';

export const ANON_COOKIE = 'relta_aid';
export const GA_CLIENT_COOKIE = 'relta_cid';
export const GCLID_COOKIE = 'relta_gclid';
export const CLICK_COOKIE = 'relta_click';
export const UTM_FIRST_TOUCH_COOKIE = 'relta_utm_ft';
export const SHARE_REF_FIRST_TOUCH_COOKIE = 'relta_ref_ft';

const TWO_YEARS = 60 * 60 * 24 * 365 * 2;
const NINETY_DAYS = 60 * 60 * 24 * 90;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GOOGLE_CLICK_PARAMS = new Set(['gclid', 'gbraid', 'wbraid']);

/** Sitemaps, robots and the llms files: machine feeds with no visitor behind them. */
const CRAWLER_FEED_PATH_RE = /^\/(sitemap[^/]*\.xml|sitemap-mls\/[^/]+|robots\.txt|llms[^/]*\.txt)$/;

export function isCrawlerFeedPath(pathname: string): boolean {
  return CRAWLER_FEED_PATH_RE.test(pathname);
}

/**
 * The browser SDK's beacon endpoint. Every tracked visitor POSTs here within
 * seconds of the first page view, which makes it the first POST that can carry
 * the script-written identity pair back for an HTTP re-issue.
 */
export const IDENTITY_REISSUE_API_PATH = '/api/analytics/ingest';

function isMutation(method: string | undefined): boolean {
  const m = (method ?? 'GET').toUpperCase();
  return m !== 'GET' && m !== 'HEAD' && m !== 'OPTIONS';
}

/**
 * Whether this request may carry attribution cookies at all. Allowed bots and
 * crawler feeds are never stamped: there is nobody to attribute, and the
 * Set-Cookie alone would make every crawl a CDN miss. API routes are left
 * alone except the analytics ingest POST, the re-issue point described above.
 */
export function shouldStampAttribution(input: { pathname: string; isAllowedBot: boolean; method?: string }): boolean {
  if (input.isAllowedBot || isCrawlerFeedPath(input.pathname)) return false;
  if (input.pathname.startsWith('/api/')) {
    return input.pathname === IDENTITY_REISSUE_API_PATH && isMutation(input.method);
  }
  return true;
}

/**
 * Honour Do Not Track and Global Privacy Control the same way the browser SDK
 * does, so the server never mints an id for someone the client would refuse to
 * track anyway.
 */
function privacySignalled(request: NextRequest): boolean {
  return request.headers.get('dnt') === '1' || request.headers.get('sec-gpc') === '1';
}

/** GA-shaped client id: `<random>.<unix seconds>`, matching the browser's. */
function mintGaClientId(): string {
  const random = Math.floor(Math.random() * 1e10);
  return `${random}.${Math.floor(Date.now() / 1000)}`;
}

/**
 * Set any missing identity/attribution cookie on the response.
 *
 * Returns true when something was written, which the caller must treat as
 * "this response is personal": a shared CDN entry carrying one visitor's
 * Set-Cookie would hand their id to everyone behind it.
 */
export function stampAttributionCookies(request: NextRequest, response: NextResponse): boolean {
  const options = {
    path: '/',
    sameSite: 'lax' as const,
    secure: request.nextUrl.protocol === 'https:',
    httpOnly: false,
  };

  // The banner writes relta_consent from script, which Safari caps at seven
  // days. Re-issue it over HTTP once, keeping the original 12-month window, so
  // iOS visitors are not asked again every week. Runs under GPC/DNT too: it
  // records a choice, it does not identify anyone.
  const consent = parseConsent(request.cookies.get(CONSENT_COOKIE)?.value);
  const consentNeedsStamp = Boolean(consent && !consent.serverStamped && consentMaxAgeLeft(consent.decidedAt) > 0);
  let wrote = false;
  if (consent && consentNeedsStamp) {
    response.cookies.set(CONSENT_COOKIE, serializeConsent(consent, true), {
      ...options,
      maxAge: consentMaxAgeLeft(consent.decidedAt),
    });
    wrote = true;
  }

  if (privacySignalled(request)) return wrote;

  const params = request.nextUrl.searchParams;
  const clickId = readClickId(params);
  const campaign = readCampaign(params);
  const shareRef = readShareRef(params);

  // The identity pair is minted only on a request that already carries a
  // reason to remember this visitor. A plain anonymous GET is left cookieless
  // so the CDN can serve it; the browser SDK fills the gap on first paint.
  const mutation = isMutation(request.method);
  const identityTrigger = mutation || consent != null || clickId != null || campaign != null || shareRef != null;
  // A POST is never CDN-cached, so it can always re-issue the existing values
  // over HTTP. That is what lifts ITP's seven-day cap off an id the browser
  // SDK wrote; the first consent stamp is a second chance to do the same.
  const reissue = mutation || consentNeedsStamp;

  if (identityTrigger) {
    const existingAnon = request.cookies.get(ANON_COOKIE)?.value;
    const anonValid = Boolean(existingAnon && UUID_RE.test(existingAnon));
    if (!anonValid || reissue) {
      response.cookies.set(ANON_COOKIE, anonValid ? existingAnon! : crypto.randomUUID(), { ...options, maxAge: TWO_YEARS });
      wrote = true;
    }

    const existingCid = request.cookies.get(GA_CLIENT_COOKIE)?.value;
    // An opt-in region visitor (Quebec) gets no analytics id before accepting,
    // and neither does a signer, who never sees the notice that implies consent.
    const analyticsAllowed = consent
      ? consent.analytics
      : !isOptInRegion(requestRegion(request)) && !isSignaturePublicPath(request.nextUrl.pathname);
    if ((!existingCid || reissue) && analyticsAllowed) {
      response.cookies.set(GA_CLIENT_COOKIE, existingCid || mintGaClientId(), { ...options, maxAge: TWO_YEARS });
      wrote = true;
    }
  }

  if (clickId) {
    // relta_gclid stays Google-only: the Ads conversion path reads it directly
    // and must not be handed a Meta id.
    if (GOOGLE_CLICK_PARAMS.has(clickId.param) && !request.cookies.get(GCLID_COOKIE)?.value) {
      response.cookies.set(GCLID_COOKIE, clickId.value, { ...options, maxAge: NINETY_DAYS });
      wrote = true;
    }
    if (!request.cookies.get(CLICK_COOKIE)?.value) {
      response.cookies.set(
        CLICK_COOKIE,
        // Not pre-encoded: cookies.set percent-encodes the value itself, and
        // encoding here too stored a double-encoded blob that the browser-side
        // readers could not parse.
        JSON.stringify({ param: clickId.param, value: clickId.value }),
        { ...options, maxAge: NINETY_DAYS },
      );
      wrote = true;
    }
  }

  if (campaign && !request.cookies.get(UTM_FIRST_TOUCH_COOKIE)?.value) {
    response.cookies.set(
      UTM_FIRST_TOUCH_COOKIE,
      JSON.stringify(campaign),
      { ...options, maxAge: NINETY_DAYS },
    );
    wrote = true;
  }

  if (shareRef && !request.cookies.get(SHARE_REF_FIRST_TOUCH_COOKIE)?.value) {
    response.cookies.set(SHARE_REF_FIRST_TOUCH_COOKIE, shareRef, { ...options, maxAge: NINETY_DAYS });
    wrote = true;
  }

  return wrote;
}

/** Region from Vercel's geo headers, falling back to an earlier relta_rgn stamp. */
function requestRegion(request: NextRequest): string | null {
  return (
    visitorRegion(request.headers.get('x-vercel-ip-country'), request.headers.get('x-vercel-ip-country-region')) ??
    request.cookies.get(REGION_COOKIE)?.value ??
    null
  );
}

/**
 * Stamps relta_rgn for opt-in regions only (Quebec), so the inline boot
 * scripts can default Google consent to denied and hold the Meta Pixel without
 * the root layout reading headers. Everyone else gets no cookie and keeps a
 * CDN-cacheable response; a stamp already present is not rewritten.
 */
export function stampRegionCookie(request: NextRequest, response: NextResponse): boolean {
  const region = visitorRegion(
    request.headers.get('x-vercel-ip-country'),
    request.headers.get('x-vercel-ip-country-region'),
  );
  if (!region || !isOptInRegion(region) || request.cookies.get(REGION_COOKIE)?.value === region) return false;
  response.cookies.set(REGION_COOKIE, region, {
    path: '/',
    sameSite: 'lax',
    secure: request.nextUrl.protocol === 'https:',
    httpOnly: false,
    maxAge: REGION_MAX_AGE_S,
  });
  return true;
}

/**
 * Parse a JSON cookie whatever percent-encoding depth it arrived at.
 *
 * The depth is not ours to control: middleware `cookies.set` serialises once,
 * the platform serialises the middleware response again, and each reader
 * decodes a different number of times on the way in (`document.cookie` raw,
 * `request.cookies.get()` pre-decoded). Cookies written by the old client-side
 * code are shallower still and are already sitting in browsers. So decode
 * until it parses instead of assuming a fixed depth.
 */
export function parseJsonCookie<T>(cookieValue: string | undefined | null): T | null {
  if (!cookieValue) return null;
  let value = cookieValue;
  for (let depth = 0; depth < 4; depth += 1) {
    try {
      return JSON.parse(value) as T;
    } catch {
      // Not JSON yet: peel one layer and try again.
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(value);
    } catch {
      return null; // malformed escape sequence
    }
    if (decoded === value) return null; // nothing left to peel
    value = decoded;
  }
  return null;
}

/** Read the stored first-touch click id back off a request. */
export function readStoredClickId(
  cookieValue: string | undefined,
): { param: string; value: string } | null {
  const parsed = parseJsonCookie<{ param?: string; value?: string }>(cookieValue);
  if (!parsed) return null;
  return parsed.param && parsed.value ? { param: parsed.param, value: parsed.value } : null;
}

const SHARE_REF_RE = /^[a-z0-9_-]{1,32}$/i;

/**
 * The `?ref=` share tag (`app_share`) a visitor first arrived with, from the
 * `relta_ref_ft` cookie. App shares carry no UTM, so without this every lead
 * they produce reads as direct. Free text is refused so it cannot carry PII
 * into the CRM.
 */
export function readShareRefFirstTouch(value: string | null | undefined): string | null {
  return value && SHARE_REF_RE.test(value) ? value.toLowerCase() : null;
}
